import { useState, useMemo, useCallback, useEffect } from "react";
import { Modal, Table, Input, Space, Typography, Tag, Empty, Spin, Tooltip, Select, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useList, useTranslate } from "@refinedev/core";
import type { ColumnsType } from "antd/es/table";
import type { FilterDropdownProps } from "antd/es/table/interface";
import type { CrudFilter } from "@refinedev/core";
import debounce from "lodash/debounce";
import { useItemTypeMetadata } from "../../hooks/useItemTypeMetadata";
import { useRelatedItems } from "../../hooks/useRelatedItems";
import { useListValues } from "../../hooks/useListValues";
import { getColumnsWithOverrides, getPickerColumns } from "../../config/gridColumns";
import { snakeToCamel } from "../../utils/fieldMapping";
import { resolveDisplayValue, isGuid } from "../../utils/displayUtils";

const { Text } = Typography;

/** Max columns to show in the picker table (keep it compact) */
const MAX_PICKER_COLUMNS = 5;

/** System columns to exclude from display */
const SYSTEM_COLUMNS = new Set(["item_number", "keyed_name", "site_id", "itemNumber", "keyedName", "siteId"]);

export interface PickedItem {
  id: string;
  keyedName?: string;
  name?: string;
  itemNumber?: string;
  state?: string;
  [key: string]: unknown;
}

interface ItemPickerModalProps {
  /** The ItemType name to search for */
  itemTypeName: string;
  /** Whether the modal is visible */
  open: boolean;
  /** Callback when modal is closed */
  onCancel: () => void;
  /** Callback when item(s) are selected */
  onSelect: (items: PickedItem[]) => void;
  /** Modal title */
  title?: string;
  /** Enable multi-select mode */
  multiSelect?: boolean;
  /** Pre-selected item IDs */
  selectedIds?: string[];
}

/**
 * Modal component for searching and picking items from an ItemType.
 * Uses grid-columns metadata from the backend for dynamic column display.
 */
export const ItemPickerModal: React.FC<ItemPickerModalProps> = ({
  itemTypeName,
  open,
  onCancel,
  onSelect,
  title,
  multiSelect = false,
  selectedIds = [],
}) => {
  const translate = useTranslate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    selectedIds as React.Key[],
  );
  const [selectedItemsMap, setSelectedItemsMap] = useState<
    Map<string, PickedItem>
  >(new Map());
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Fetch grid-columns metadata
  const { gridColumns, isLoading: isMetadataLoading } = useItemTypeMetadata({
    itemTypeName,
    enabled: open,
  });

  // All processed columns (excluding system)
  const allProcessedColumns = useMemo(() => {
    if (gridColumns.length === 0) return [];
    const processed = getColumnsWithOverrides(gridColumns, itemTypeName);
    return processed.filter((col) => !SYSTEM_COLUMNS.has(col.name));
  }, [gridColumns, itemTypeName]);

  // Visible table columns: use pickerColumns config if available, otherwise first N
  const visibleGridColumns = useMemo(() => {
    const pickerCols = getPickerColumns(itemTypeName);
    if (pickerCols) {
      const colMap = new Map(allProcessedColumns.map((c) => [c.name, c]));
      return pickerCols
        .map((name) => colMap.get(name))
        .filter((c): c is NonNullable<typeof c> => c != null);
    }
    return allProcessedColumns.slice(0, MAX_PICKER_COLUMNS);
  }, [allProcessedColumns, itemTypeName]);

  // Column options for the scope Select dropdown (searchable types only)
  const columnOptions = useMemo(
    () =>
      visibleGridColumns
        .filter(
          (col) =>
            col.dataType === "string" ||
            col.dataType === "text" ||
            col.dataType === "integer" ||
            col.dataType === "float" ||
            col.dataType === "decimal" ||
            (col.dataType === "item" && col.dataSource),
        )
        .map((col) => ({
          label: translate(`table.${col.name}`, col.label),
          value: col.name,
        })),
    [visibleGridColumns, translate],
  );

  // Detect item-reference columns for filter dropdowns — only from visible picker columns
  const itemRefFilterColumns = useMemo(() => {
    if (visibleGridColumns.length === 0) return [];
    return visibleGridColumns.filter(
      (col) => col.dataType === "item" && col.dataSource && !SYSTEM_COLUMNS.has(col.name),
    );
  }, [visibleGridColumns]);

  // State for item-reference filters (field name -> selected value)
  const [refFilterValues, setRefFilterValues] = useState<Record<string, string | undefined>>({});

  // Fetch options for item-reference filter dropdowns
  const relatedItemConfigs = useMemo(
    () => itemRefFilterColumns.map((col) => ({ itemType: col.dataSource! })),
    [itemRefFilterColumns],
  );
  const { items: refFilterOptions, isLoading: isRefFiltersLoading } = useRelatedItems({
    relatedItems: relatedItemConfigs,
    enabled: open && relatedItemConfigs.length > 0,
  });

  // Fetch list values for list-type columns (needed for rendering labels)
  const listNames = useMemo(
    () => [
      ...new Set(
        visibleGridColumns
          .filter(
            (col) =>
              (col.dataType === "list" || col.dataType === "filter list" || col.dataType === "mv_list") &&
              col.dataSource,
          )
          .map((col) => col.dataSource as string),
      ),
    ],
    [visibleGridColumns],
  );
  const { lists } = useListValues({ listNames, enabled: open && listNames.length > 0 });

  // Debounce search input
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 300),
    [],
  );

  // Cancel pending debounce on unmount
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchText(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const handleColumnChange = useCallback(
    (value: string | undefined) => {
      setSearchColumn(value);
      setPagination((prev) => ({ ...prev, current: 1 }));
    },
    [],
  );

  // State for column header filters (field name -> filter value)
  const [columnFilters, setColumnFilters] = useState<Record<string, string | null>>({});

  // Build filters using search/searchField params (same pattern as GenericList)
  // This ensures Vietnamese accent-insensitive search via COLLATE on the backend
  const combinedFilters = useMemo(() => {
    const filters: CrudFilter[] = [];
    if (debouncedSearch) {
      filters.push({ field: "search", operator: "eq", value: debouncedSearch });
      if (searchColumn) {
        filters.push({ field: "searchField", operator: "eq", value: searchColumn });
      }
    }
    // Add item-reference filters (e.g., educationFacility=<id>)
    for (const [field, value] of Object.entries(refFilterValues)) {
      if (value) {
        filters.push({ field: snakeToCamel(field), operator: "eq", value });
      }
    }
    // Add column header filters (server-side)
    for (const [field, value] of Object.entries(columnFilters)) {
      if (value) {
        // For item-ref columns, use exact match (GUID). For others, use contains.
        const col = visibleGridColumns.find((c) => c.name === field);
        if (col?.dataType === "item" || col?.dataType === "list" || col?.dataType === "filter list") {
          filters.push({ field: snakeToCamel(field), operator: "eq", value });
        } else {
          filters.push({ field: snakeToCamel(field), operator: "contains", value });
        }
      }
    }
    return filters.length > 0 ? filters : undefined;
  }, [debouncedSearch, searchColumn, refFilterValues, columnFilters, visibleGridColumns]);

  // Fetch items using Refine's useList hook
  const { data, isLoading, isError } = useList({
    resource: itemTypeName,
    pagination: {
      current: pagination.current,
      pageSize: pagination.pageSize,
    },
    filters: combinedFilters,
    queryOptions: {
      enabled: open,
    },
  });

  // Build columns from grid-columns metadata
  const columns = useMemo<ColumnsType<PickedItem>>(() => {
    if (visibleGridColumns.length === 0) {
      return [
        {
          title: translate("fields.name", "Name"),
          key: "name",
          render: (_: unknown, record: PickedItem) => {
            const displayName = record.keyedName || record.name || record.id;
            return <Text strong>{displayName}</Text>;
          },
          ellipsis: true,
        },
      ];
    }

    // Build filter props for a column (server-side via combinedFilters)
    const buildPickerFilter = (
      col: typeof visibleGridColumns[0],
    ): Partial<ColumnsType<PickedItem>[0]> => {
      const filterValue = columnFilters[col.name];
      const filteredValue = filterValue ? [filterValue] : null;

      // List columns: Select dropdown
      if (
        (col.dataType === "list" || col.dataType === "filter list") &&
        col.dataSource
      ) {
        const listVals = col.dataSource ? lists[col.dataSource] : undefined;
        if (listVals && listVals.length > 0) {
          return {
            filteredValue,
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
            }: FilterDropdownProps) => (
              <div style={{ padding: 8 }}>
                <Select
                  style={{ width: 200, marginBottom: 8, display: "block" }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={selectedKeys[0] as string}
                  onChange={(val) => setSelectedKeys(val ? [val] : [])}
                  placeholder={translate("table.selectFilter", "Chọn...")}
                  options={listVals.map((lv) => ({
                    label: lv.label || lv.value,
                    value: lv.value,
                  }))}
                  getPopupContainer={(trigger) =>
                    trigger.closest(".ant-modal-body") || document.body
                  }
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={() => confirm()}
                    size="small"
                    style={{ width: 90 }}
                  >
                    {translate("buttons.filter", "Lọc")}
                  </Button>
                  <Button
                    onClick={() => {
                      clearFilters?.();
                      confirm();
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    {translate("buttons.clear", "Xóa")}
                  </Button>
                </Space>
              </div>
            ),
          };
        }
      }

      // Text filter for string, item, date, integer, etc.
      return {
        filteredValue,
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: FilterDropdownProps) => (
          <div style={{ padding: 8 }}>
            <Input
              value={selectedKeys[0] as string}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              placeholder={translate("table.searchName", "Tìm kiếm...")}
              style={{ width: 200, marginBottom: 8, display: "block" }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                {translate("buttons.filter", "Lọc")}
              </Button>
              <Button
                onClick={() => {
                  clearFilters?.();
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                {translate("buttons.clear", "Xóa")}
              </Button>
            </Space>
          </div>
        ),
      };
    };

    return visibleGridColumns.map((col, index) => {
      const dataIndex = snakeToCamel(col.name);
      const isFirstColumn = index === 0;

      return {
        key: col.name,
        dataIndex,
        title: translate(`table.${col.name}`, col.label),
        width: col.width ? Math.min(col.width, 200) : undefined,
        ellipsis: { showTitle: false },
        ...buildPickerFilter(col),
        render: (value: unknown, record: PickedItem) => {
          // Resolve display text based on column data type
          let displayText: string | null = null;

          if (col.dataType === "list" || col.dataType === "filter list") {
            if (!value) return <Text type="secondary">-</Text>;
            const valueStr = String(value);
            const listVals = col.dataSource ? lists[col.dataSource] : undefined;
            displayText = listVals?.find((lv) => lv.value === valueStr)?.label || valueStr;
            if (!isFirstColumn) {
              return (
                <Tooltip title={displayText} placement="topLeft">
                  <Tag>{displayText}</Tag>
                </Tooltip>
              );
            }
          } else if (col.dataType === "item") {
            // Item reference: prefer Display field, never show raw GUID
            const displayFieldName = dataIndex + "Display";
            const resolved = resolveDisplayValue(value, record[displayFieldName]);
            displayText = resolved === "-" ? null : resolved;
          } else if (value && !isGuid(value)) {
            displayText = String(value);
          }

          // If no display text resolved, show dash
          if (!displayText) return <Text type="secondary">-</Text>;

          // First column: bold styling
          if (isFirstColumn) {
            return (
              <Tooltip title={displayText} placement="topLeft">
                <Text strong>{displayText}</Text>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={displayText} placement="topLeft">
              <Text>{displayText}</Text>
            </Tooltip>
          );
        },
      };
    });
  }, [visibleGridColumns, translate, lists, columnFilters]);

  // Sync selected items map when row selection changes
  const updateSelectedItems = useCallback(
    (keys: React.Key[], currentPageData?: PickedItem[]) => {
      setSelectedRowKeys(keys);
      setSelectedItemsMap((prev) => {
        const next = new Map(prev);
        const keySet = new Set(keys.map(String));
        for (const id of prev.keys()) {
          if (!keySet.has(id)) next.delete(id);
        }
        const pageData = currentPageData || (data?.data as PickedItem[]) || [];
        for (const item of pageData) {
          if (keySet.has(item.id) && !next.has(item.id)) {
            next.set(item.id, item);
          }
        }
        return next;
      });
    },
    [data?.data],
  );

  const rowSelection = {
    type: (multiSelect ? "checkbox" : "radio") as "checkbox" | "radio",
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      updateSelectedItems(newSelectedRowKeys);
    },
  };

  const handleRowClick = (record: PickedItem) => {
    const recordKey = record.id as React.Key;
    if (multiSelect) {
      const newKeys = selectedRowKeys.includes(recordKey)
        ? selectedRowKeys.filter((key) => key !== recordKey)
        : [...selectedRowKeys, recordKey];
      updateSelectedItems(newKeys, [record]);
    } else {
      updateSelectedItems([recordKey], [record]);
    }
  };

  const handleRowDoubleClick = (record: PickedItem) => {
    onSelect([record]);
    handleClose();
  };

  const handleOk = () => {
    if (selectedRowKeys.length === 0) return;
    const selectedItems = Array.from(selectedItemsMap.values());
    onSelect(selectedItems);
    handleClose();
  };

  const handleClose = () => {
    setSearchText("");
    setDebouncedSearch("");
    setSearchColumn(undefined);
    setRefFilterValues({});
    setColumnFilters({});
    setSelectedRowKeys([]);
    setSelectedItemsMap(new Map());
    setPagination({ current: 1, pageSize: 10 });
    onCancel();
  };

  const handleTableChange = (
    paginationConfig: { current?: number; pageSize?: number },
    filters?: Record<string, (React.Key | boolean)[] | null>,
  ) => {
    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    });
    // Sync column header filter state for server-side filtering
    if (filters) {
      const newColumnFilters: Record<string, string | null> = {};
      for (const [key, values] of Object.entries(filters)) {
        newColumnFilters[key] = values && values.length > 0 ? String(values[0]) : null;
      }
      setColumnFilters(newColumnFilters);
      // Reset to page 1 when filters change
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const translatedItemType = translate(
    `navigation.${itemTypeName}`,
    itemTypeName,
  );
  const modalTitle =
    title || `${translate("buttons.select", "Select")} ${translatedItemType}`;

  // Placeholder changes based on selected column
  const selectedColLabel = columnOptions.find((o) => o.value === searchColumn)?.label;
  const searchPlaceholder = selectedColLabel
    ? translate("itemPicker.searchByColumn", { column: selectedColLabel }, `Search by ${selectedColLabel}...`)
    : translate("itemPicker.searchPlaceholder", "Search by name, number, or description...");

  return (
    <Modal
      title={modalTitle}
      open={open}
      onOk={handleOk}
      onCancel={handleClose}
      width={800}
      okText={translate("buttons.select", "Select")}
      cancelText={translate("buttons.cancel", "Cancel")}
      okButtonProps={{
        disabled: selectedRowKeys.length === 0,
      }}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Space.Compact block>
          <Select
            style={{ width: 180 }}
            allowClear
            placeholder={translate("itemPicker.allColumns", "All columns")}
            value={searchColumn}
            onChange={handleColumnChange}
            options={columnOptions}
            getPopupContainer={(trigger) => trigger.closest(".ant-modal-body") || document.body}
          />
          <Input
            placeholder={searchPlaceholder}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearchChange}
            allowClear
            style={{ flex: 1 }}
          />
        </Space.Compact>

        {/* Item-reference filter dropdowns (e.g., filter Student by EducationFacility) */}
        {itemRefFilterColumns.length > 0 && (
          <Space wrap size="small">
            {itemRefFilterColumns.map((col) => {
              const options = col.dataSource ? refFilterOptions[col.dataSource] || [] : [];
              return (
                <Select
                  key={col.name}
                  style={{ minWidth: 200 }}
                  allowClear
                  showSearch
                  loading={isRefFiltersLoading}
                  placeholder={translate(`table.${col.name}`, col.label)}
                  value={refFilterValues[col.name]}
                  onChange={(value) => {
                    setRefFilterValues((prev) => ({ ...prev, [col.name]: value }));
                    setPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                  options={options.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  getPopupContainer={(trigger) => trigger.closest(".ant-modal-body") || document.body}
                />
              );
            })}
          </Space>
        )}

        {isError ? (
          <Empty
            description={translate("errors.fetchFailed", "Failed to load data")}
          />
        ) : isLoading || isMetadataLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={data?.data as PickedItem[]}
            columns={columns}
            rowKey="id"
            rowSelection={rowSelection}
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data?.total,
              showSizeChanger: true,
              showTotal: (total) =>
                translate("common.totalItems", { total }, `${total} items`),
            }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              onDoubleClick: () => handleRowDoubleClick(record),
              style: { cursor: "pointer" },
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={translate("itemPicker.noItems", "No items found")}
                />
              ),
            }}
          />
        )}

        {multiSelect && selectedRowKeys.length > 0 && (
          <Text type="secondary">
            {translate(
              "itemPicker.selectedCount",
              { count: selectedRowKeys.length },
              `${selectedRowKeys.length} item(s) selected`,
            )}
          </Text>
        )}
      </Space>
    </Modal>
  );
};

export default ItemPickerModal;
